import { defineStore } from "pinia";
import { ref } from "vue";
import {
  Notebook,
  NotebookPage,
  NotebookPageApiModel,
  NotebookPageNote,
  NotebookPageNoteApiModel,
  NotebookPageStructure,
} from "@modules/notebook-editor/notebook-editor-types.ts";
import notebookApi from "@modules/notebook-editor/notebook-editor-api.ts";
import notebookPageApi from "@apis/notebook-page-api.ts";
import notebookPageNoteApi from "@apis/notebook-page-note.api.ts";
import { isEmpty } from "@utils/var.check.ts";

export const useNotebookEditorStore = defineStore("notebookEditor", () => {
  // state
  const notebook = ref<Notebook | null>(null); // null or a notebook object
  const notebookPageStructureById = ref<Record<number, NotebookPageStructure>>({}); // Notebook page structures with minimum info
  const notebookPageOrder = ref<number[]>([]); // Array of page IDs in orders
  const notebookPageById = ref<Record<number, NotebookPage>>({}); // Page objects by their IDs
  const notebookPageNoteOrderByPageId = ref<Record<number, number[]>>({}); // Array of note IDs in orders
  const notebookPageNoteById = ref<Record<number, NotebookPageNote>>({}); // Notes objects by their IDs
  const spotlightMenuOpen = ref(false);
  const savingComponentState = ref({
    key: null as string | null,
    promiseResolveRule: [] as { name: string; resolved: boolean }[],
    actionPromise: null as { resolve: () => void; reject: () => void } | null,
  });

  // state - editing component
  const editingComponentId = ref<string | null>(null);

  // state - is notebook editor left panel closed (default) or expanded
  const notebookLeftPanelExpanded = ref<boolean>(false);

  // getters
  const getNotebook = () => {
    return notebook.value;
  };
  const getNotebookPageStructureMapByPageId = (pageId: number) => {
    return notebookPageStructureById.value[pageId];
  };
  const getOrderedNotebookPageStructures = () => {
    return notebookPageOrder.value
      .map((pageId) => notebookPageStructureById.value[pageId])
      .filter(Boolean);
  };
  const getNotebookPageOrder = () => {
    return notebookPageOrder.value;
  };
  const getNotebookPageById = (id: number) => {
    return notebookPageById.value[id];
  };
  const getNotebookPageIndexByPageId = (pageId: number | 0) => {
    return getNotebookPageOrder().indexOf(pageId);
  };
  const getNotebookPageNotesByPageId = (pageId: number | 0) => {
    // Get the note IDs associated with the pageId
    const noteIds = notebookPageNoteOrderByPageId.value[pageId];

    // If there are no notes for this pageId, return an empty array
    if (!noteIds) {
      return [];
    }

    // Map the note IDs to the corresponding notes
    return noteIds.map((noteId) => notebookPageNoteById.value[noteId]).filter(Boolean);
  };
  const getNotebookPageNoteCountByPageId = (pageId: number | 0) => {
    // Get the note IDs associated with the pageId
    const noteIds = notebookPageNoteOrderByPageId.value[pageId];
    return noteIds?.length ?? 0;
  };
  const getNotebookPageNoteLastNoteIndex = (pageId: number | 0) => {
    const noteIds = notebookPageNoteOrderByPageId.value[pageId];
    return noteIds && noteIds.length > 0 ? noteIds.length - 1 : 0;
  };
  const getNotebookPageNoteCurrentIndex = (pageId: number | 0, noteId: number | 0) => {
    return notebookPageNoteOrderByPageId.value[pageId].indexOf(noteId);
  };
  const getNotebookPageNoteOrder = (pageId: number) => {
    return notebookPageNoteOrderByPageId.value[pageId];
  };
  const getTargetPageIndex = (pageId: number | null) => {
    if (pageId === null) return 0;
    const idx = getNotebookPageIndexByPageId(pageId);
    return idx >= 0 ? idx + 1 : 0;
  };

  const componentMode = (componentId: string) => {
    return editingComponentId.value === componentId ? "edit" : "view";
  };
  const isComponentViewMode = (componentId: string) => {
    return componentMode(componentId) === "view";
  };
  const isComponentEditMode = (componentId: string) => {
    return componentMode(componentId) === "edit";
  };
  const hasActiveEditingComponent = () => {
    return editingComponentId.value !== null;
  };

  const isCurrentActiveEditingComponent = (componentId: string) => {
    return editingComponentId.value !== null && editingComponentId.value === componentId;
  };

  const isNotebookLeftPanelExpanded = () => {
    return notebookLeftPanelExpanded.value;
  };

  // actions
  const setNotebook = (newNotebook: Notebook) => {
    notebook.value = newNotebook;
  };
  const addPage = (newPage: NotebookPage) => {
    notebookPageById.value[newPage.id] = newPage;
  };
  const addPageToPageOrder = (newPageId: number, targetPageIndex: number) => {
    if (!notebookPageOrder.value) {
      notebookPageOrder.value = [];
    }
    notebookPageOrder.value.splice(targetPageIndex, 0, newPageId);
  };
  const addNoteToPageOrder = (pageId: number, newNoteId: number, targetNoteIndex: number) => {
    if (!notebookPageNoteOrderByPageId.value[pageId]) {
      // If the pageId does not exist, initialize it with an empty array
      notebookPageNoteOrderByPageId.value[pageId] = [];
    }
    notebookPageNoteOrderByPageId.value[pageId].splice(targetNoteIndex, 0, newNoteId);
  };
  const addNote = (pageNote: NotebookPageNote) => {
    notebookPageNoteById.value[pageNote.id] = pageNote;
  };
  const addNotebookPageStructure = (notebookPage: NotebookPage) => {
    const pageStructure = {
      id: notebookPage.id,
      uuid: notebookPage.uuid,
      title: notebookPage.title,
    };
    notebookPageStructureById.value[notebookPage.id] = pageStructure;
  };
  const updateNotebookPageStructure = (notebookPage: NotebookPage) => {
    const pageStructure = notebookPageStructureById.value[notebookPage.id];
    if (pageStructure) {
      pageStructure.title = notebookPage.title;
    }
  };
  const updatePageOrder = (pageOrder: number[]) => {
    notebookPageOrder.value = pageOrder;
  };
  const updatePageNoteOrder = (pageId: number, noteOrder: number[]) => {
    notebookPageNoteOrderByPageId.value[pageId] = noteOrder;
  };
  const shiftPageOrder = (fromIndex: number, toIndex: number) => {
    const originalOrder = notebookPageOrder.value;
    const copiedOrder = originalOrder ? [...originalOrder] : [];

    if (
      !copiedOrder ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= copiedOrder.length ||
      toIndex >= copiedOrder.length
    ) {
      return;
    }

    // Remove page from current position
    const [movedPageId] = copiedOrder.splice(fromIndex, 1);
    // Insert it at new position
    copiedOrder.splice(toIndex, 0, movedPageId);

    return copiedOrder;
  };
  const shiftNoteOrder = (pageId: number, fromIndex: number, toIndex: number) => {
    const originalOrder = notebookPageNoteOrderByPageId.value[pageId];
    const copiedOrder = originalOrder ? [...originalOrder] : [];

    if (
      !copiedOrder ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= copiedOrder.length ||
      toIndex >= copiedOrder.length
    ) {
      return;
    }

    // Remove note from current position
    const [movedNoteId] = copiedOrder.splice(fromIndex, 1);
    // Insert it at new position
    copiedOrder.splice(toIndex, 0, movedNoteId);

    return [...copiedOrder];
  };
  const removePageById = (pageId: number) => {
    delete notebookPageById.value[pageId];
  };
  const removeNotebookPageStructureById = (pageId: number) => {
    delete notebookPageStructureById.value[pageId];
  };
  const removePageNoteOrderByPageId = (pageId: number) => {
    delete notebookPageNoteOrderByPageId.value[pageId];
  };
  const removePageNoteOrderByPageIdAndNoteId = (pageId: number, noteId: number) => {
    if (notebookPageNoteOrderByPageId.value[pageId]) {
      const noteIndex = notebookPageNoteOrderByPageId.value[pageId].indexOf(noteId);

      if (noteIndex !== -1) {
        notebookPageNoteOrderByPageId.value[pageId].splice(noteIndex, 1);

        // If the array is empty after removal, optionally delete the pageId
        if (notebookPageNoteOrderByPageId.value[pageId].length === 0) {
          delete notebookPageNoteOrderByPageId.value[pageId];
        }
      }
    }
  };
  const removeNoteById = (noteId: number) => {
    delete notebookPageNoteById.value[noteId];
  };

  const addSavingComponentPromise = ({
    resolve,
    reject,
  }: {
    resolve: () => void;
    reject: () => void;
  }) => {
    savingComponentState.value.actionPromise = { resolve: resolve, reject: reject };
  };
  const updateSavingComponentRule = (updatedRule: string) => {
    for (const rule of savingComponentState.value?.promiseResolveRule) {
      if (rule.name === updatedRule) {
        rule.resolved = true;
        break;
      }
    }
  };
  const updateSavingComponentPromise = () => {
    if (savingComponentState.value.actionPromise) {
      savingComponentState.value.actionPromise.resolve();
    }
  };
  const setNotebookEditorLeftPanelExpanded = (isExpanded: boolean) => {
    notebookLeftPanelExpanded.value = isExpanded;
  };

  // actions - API call
  const fetchNotebook = async (notebookUuid: string) => {
    try {
      const { code, data, errorMap } = await notebookApi.fetchNotebook(notebookUuid);
      if (code !== "ok") {
        const error = Object.values(errorMap)[0] ?? "";
        throw new Error(error);
      }

      const notebook = data;
      setNotebook(notebook);
      const rawOrder = notebook.pageOrder ?? "";
      if (!isEmpty(rawOrder)) {
        notebookPageOrder.value = rawOrder.split(",").map((s) => Number(s));
      }
    } catch (error) {
      throw error;
    }
  };

  const updateNotebook = async (updatedNotebook: Notebook) => {
    try {
      const { code, data, errorMap } = await notebookApi.updateNotebook(updatedNotebook);
      if (code !== "ok") {
        const error = Object.values(errorMap)[0] ?? "";
        throw new Error(error);
      }

      const notebook = data;
      setNotebook(notebook);
    } catch (error) {
      throw error;
    }
  };

  const fetchNotebookStructure = async (uuid: string) => {
    try {
      const { code, data, errorMap } = await notebookApi.fetchNotebookStructure(uuid);
      if (code !== "ok") {
        const error = Object.values(errorMap)[0] ?? "";
        throw new Error(error);
      }

      const pageStructures = data;
      notebookPageStructureById.value = pageStructures?.pages.reduce(
        (acc: Record<number, NotebookPageStructure>, page: NotebookPageStructure) => {
          acc[page.id] = page;
          return acc;
        },
        {},
      );
    } catch (error) {
      throw error;
    }
  };

  const fetchNotebookPage = async (pageId: number) => {
    try {
      const page = notebookPageStructureById.value[pageId];
      const pageUuid = page.uuid;
      const { code, data, errorMap } = await notebookPageApi.fetchNotebookPage(pageUuid);
      if (code !== "ok" || !data || !data.id) {
        const error = Object.values(errorMap)[0] ?? "";
        throw new Error(error);
      }

      addPage(data);
      const pageNoteOrder = data?.noteOrder;
      if (pageNoteOrder) {
        const noteOrderArray: number[] = pageNoteOrder.split(",").map(Number);
        updatePageNoteOrder(data.id, noteOrderArray);
      }
    } catch (error) {
      throw error;
    }
  };

  const createNotebookPage = async (payload: NotebookPageApiModel) => {
    try {
      const { code, data, errorMap } = await notebookPageApi.createNotebookPage(payload);
      if (code !== "ok") {
        const error = Object.values(errorMap)[0] ?? "";
        throw new Error(error);
      }

      const newPage = data;
      const newPageId = newPage.id;
      const targetPageIndex = payload.targetPageIndex;
      if (!newPageId) {
        throw new Error();
      }

      addPageToPageOrder(newPageId, targetPageIndex);
      addNotebookPageStructure(newPage);
      addPage(newPage);
      return newPage;
    } catch (error) {
      throw error;
    }
  };

  const updateNotebookPage = async (updatedNotebookPage: NotebookPage) => {
    try {
      const { code, data, errorMap } =
        await notebookPageApi.updateNotebookPage(updatedNotebookPage);
      if (code !== "ok") {
        const error = Object.values(errorMap)[0] ?? "";
        throw new Error(error);
      }

      const page = data;
      addPage(page);
      updateNotebookPageStructure(page);
    } catch (error) {
      throw error;
    }
  };

  const deleteNotebookPage = async (uuid: string) => {
    try {
      const { code, errorMap } = await notebookPageApi.deleteNotebookPage(uuid);
      if (code !== "ok") {
        const error = Object.values(errorMap)[0] ?? "";
        throw new Error(error);
      }

      const page = Object.values(notebookPageById.value).find((p) => p.uuid === uuid);
      const pageId = page?.id;
      if (!page || !pageId) {
        return null;
      }

      const oldOrder = [...notebookPageOrder.value];
      const idx = oldOrder.indexOf(pageId);
      const newOrder = oldOrder.filter((id) => id !== pageId);
      let nextId: number | null = null;
      if (newOrder.length) {
        const nextIdx = idx < newOrder.length ? idx : newOrder.length - 1;
        nextId = newOrder[nextIdx] ?? null;
      }

      updatePageOrder(newOrder);
      removePageById(pageId);
      removeNotebookPageStructureById(pageId);
      removePageNoteOrderByPageId(pageId);
      return nextId;
    } catch (error) {
      throw error;
    }
  };

  const reorderNotebookPage = async (pageId: number, moveUp: boolean) => {
    try {
      const page = getNotebookPageStructureMapByPageId(pageId);
      const pageUuid = page.uuid;
      const currentIndex = notebookPageOrder.value.indexOf(pageId);
      const newIndex = moveUp ? currentIndex - 1 : currentIndex + 1;
      const newPageOrder = shiftPageOrder(currentIndex, newIndex);
      const newPageOrderString = newPageOrder?.join(",") ?? "";
      const { code, errorMap } = await notebookPageApi.reorderNotebookPage(
        pageUuid,
        newPageOrderString,
      );
      if (code !== "ok") {
        const error = Object.values(errorMap)[0] ?? "";
        throw new Error(error);
      }

      updatePageOrder(newPageOrder ?? []);
    } catch (error) {
      throw error;
    }
  };

  const fetchNotebookPageNotes = async (pageUuid: string) => {
    try {
      const { code, data, errorMap } = await notebookPageNoteApi.fetchNotebookPageNotes(pageUuid);
      if (code !== "ok") {
        const error = Object.values(errorMap)[0] ?? "";
        throw new Error(error);
      }

      const pageNotes = data;
      notebookPageNoteById.value = pageNotes.reduce(
        (acc: Record<number, NotebookPageNote>, note: NotebookPageNote) => {
          acc[note.id] = note;
          return acc;
        },
        {},
      );
    } catch (error) {
      throw error;
    }
  };

  const createNotebookPageNote = async (pageNoteApiModel: NotebookPageNoteApiModel) => {
    try {
      const { code, data, errorMap } =
        await notebookPageNoteApi.createNotebookPageNote(pageNoteApiModel);
      if (code !== "ok") {
        const error = Object.values(errorMap)[0] ?? "";
        throw new Error(error);
      }

      // remove dummy page note and page note order
      const pageId = pageNoteApiModel?.pageId;
      if (typeof pageId !== "number") {
        throw new Error();
      }

      removeNoteById(0);
      removePageNoteOrderByPageIdAndNoteId(pageId, 0);

      // update new page note order with new note id
      const pageNote = data;
      const targetNoteIndex = pageNoteApiModel.targetNoteIndex;
      if (typeof targetNoteIndex !== "number") {
        throw new Error();
      }

      addNote(pageNote);
      addNoteToPageOrder(pageId, pageNote.id, targetNoteIndex);
      return pageNote;
    } catch (error) {
      throw error;
    }
  };

  const updateNotebookPageNote = async (
    noteUuid: string,
    pageNoteApiModel: NotebookPageNoteApiModel,
  ) => {
    try {
      const { code, data, errorMap } = await notebookPageNoteApi.updateNotebookPageNote(
        noteUuid,
        pageNoteApiModel,
      );
      if (code !== "ok") {
        const error = Object.values(errorMap)[0] ?? "";
        throw new Error(error);
      }

      const pageNote = data;
      addNote(pageNote);
    } catch (error) {
      throw error;
    }
  };

  const deleteNotebookPageNote = async (note: NotebookPageNote) => {
    try {
      const noteId = note.id;
      const noteUuid = note.uuid;
      const pageId = note.notebookPageId;

      if (!isEmpty(noteUuid)) {
        const { code, errorMap } = await notebookPageNoteApi.deleteNotebookPageNote(noteUuid);
        if (code !== "ok") {
          const error = Object.values(errorMap)[0] ?? "";
          throw new Error(error);
        }
      }

      removeNoteById(noteId);
      removePageNoteOrderByPageIdAndNoteId(pageId, noteId);
    } catch (error) {
      throw error;
    }
  };

  const reorderNotebookPageNote = async (note: NotebookPageNote, moveUp: boolean) => {
    try {
      const noteId = note.id;
      const noteUuid = note.uuid;
      const pageId = note.notebookPageId;
      const currentIndex = notebookPageNoteOrderByPageId.value[pageId].indexOf(noteId);
      const newIndex = moveUp ? currentIndex - 1 : currentIndex + 1;
      const newNoteOrder = shiftNoteOrder(pageId, currentIndex, newIndex) as number[];
      const newNoteOrderString = newNoteOrder?.join(",") as string;
      const { code, errorMap } = await notebookPageNoteApi.reorderNotebookPageNote(
        noteUuid,
        newNoteOrderString,
      );
      if (code !== "ok") {
        const error = Object.values(errorMap)[0] ?? "";
        throw new Error(error);
      }

      updatePageNoteOrder(pageId, newNoteOrder);
    } catch (error) {
      throw error;
    }
  };

  const moveNotebookPageNoteToSpecificPage = async (
    note: NotebookPageNote,
    targetIndexNo: number,
  ) => {
    try {
      const targetPage = getOrderedNotebookPageStructures()[targetIndexNo];
      const { code, data, errorMap } = await notebookPageNoteApi.moveNotebookPageNoteSpecificPage(
        targetPage.id,
        note.uuid,
      );
      if (code !== "ok") {
        const error = Object.values(errorMap)[0] ?? "";
        throw new Error(error);
      }
      note.notebookPageId = targetPage.id;
      addNote(note);
      data.forEach((page) => {
        const noteOrderArray: number[] = page.noteOrder?.split(",").map(Number) ?? [];
        updatePageNoteOrder(page.id, noteOrderArray);
      });
    } catch (error) {
      throw error;
    }
  };

  const moveNotebookPageToSpecificPage = async (pageId: number, targetIndexNo: number) => {
    try {
      const page = getNotebookPageStructureMapByPageId(pageId);
      const pageUuid = page.uuid;
      const pageOrder = notebookPageOrder.value;
      const currentIndex = pageOrder.indexOf(pageId);
      const [item] = pageOrder.splice(currentIndex, 1);
      pageOrder.splice(targetIndexNo, 0, item);
      const { code, errorMap } = await notebookPageApi.reorderNotebookPage(
        pageUuid,
        pageOrder?.join(",") ?? "",
      );
      if (code !== "ok") {
        const error = Object.values(errorMap)[0] ?? "";
        throw new Error(error);
      }

      updatePageOrder(pageOrder);
      return;
    } catch (error) {
      throw error;
    }
  };

  const setEditingComponentId = (newId: string) => {
    editingComponentId.value = newId;
  };

  const resetEditingComponentId = () => {
    editingComponentId.value = null;
  };

  const registerSavingComponent = (id: string, rules: string[]) => {
    savingComponentState.value.key = id;

    const ruleValues = rules?.map((rule) => {
      return {
        name: rule,
        resolved: false,
      };
    });
    savingComponentState.value.promiseResolveRule = ruleValues;
  };

  const deregisterSavingComponent = () => {
    savingComponentState.value.key = null;
    savingComponentState.value.promiseResolveRule = [];
    savingComponentState.value.actionPromise = null;
  };

  const getSavingComponentPromise = (): Promise<void> => {
    const savingComponentStateValue = savingComponentState.value;

    if (!savingComponentStateValue.key) {
      return Promise.resolve();
    }

    if (savingComponentStateValue.actionPromise) {
      return Promise.reject();
    }

    let resolveFn!: () => void;
    let rejectFn!: () => void;

    const promise = new Promise<void>((resolve, reject) => {
      resolveFn = resolve;
      rejectFn = reject;
    });

    addSavingComponentPromise({ resolve: resolveFn, reject: rejectFn });
    return promise;
  };

  const updateSavingComponentState = (rule: string) => {
    const promiseRules = savingComponentState?.value?.promiseResolveRule;
    if (promiseRules && Array.isArray(promiseRules)) {
      updateSavingComponentRule(rule);
      const allResolved = savingComponentState.value.promiseResolveRule.every((rule) => {
        return rule.resolved;
      });
      if (!allResolved) {
        return;
      }
    }
    updateSavingComponentPromise();
    deregisterSavingComponent();
  };

  const fetchNotebookPageNoteAnnotation = async (noteUuid: string) => {
    try {
      const { code, data, errorMap } =
        await notebookPageNoteApi.fetchNotebookPageNoteAnnotation(noteUuid);
      if (code !== "ok") {
        const error = Object.values(errorMap)[0] ?? "";
        throw new Error(error);
      }
      return data;
    } catch (error) {
      throw error;
    }
  };
  const updateNotebookPageSpotlight = async (uuid: string, spotlightColor: string) => {
    try {
      const { code, errorMap } = await notebookPageApi.updateNotebookPageSpotlight(
        uuid,
        spotlightColor,
      );
      if (code !== "ok") {
        const error = Object.values(errorMap)[0] ?? "";
        throw new Error(error);
      }

      const page = Object.values(notebookPageStructureById.value).find((p) => p.uuid === uuid);
      if (page) {
        page.spotlightColor = spotlightColor;
      }
    } catch (error) {
      throw error;
    }
  };

  const deleteNotebookPageSpotlight = async (uuid: string) => {
    try {
      const pageHasColor = Object.values(notebookPageStructureById.value).find(
        (page) => page.spotlightColor && page.uuid === uuid,
      );
      if (!pageHasColor) return;

      const { code, errorMap } = await notebookPageApi.deleteNotebookPageSpotlight(uuid);
      if (code !== "ok") {
        const error = Object.values(errorMap)[0] ?? "";
        throw new Error(error);
      }
      pageHasColor.spotlightColor = null;
    } catch (error) {
      throw error;
    }
  };

  const toggleSpotlightMenu = () => {
    spotlightMenuOpen.value = !spotlightMenuOpen.value;
  };

  const closeSpotlightMenu = () => {
    spotlightMenuOpen.value = false;
  };

  return {
    getNotebook,
    getOrderedNotebookPageStructures,
    getNotebookPageStructureMapByPageId,
    getNotebookPageOrder,
    getNotebookPageById,
    getNotebookPageIndexByPageId,
    getNotebookPageNotesByPageId,
    getNotebookPageNoteCountByPageId,
    getNotebookPageNoteLastNoteIndex,
    getNotebookPageNoteCurrentIndex,
    getNotebookPageNoteOrder,
    getTargetPageIndex,
    isComponentViewMode,
    isComponentEditMode,
    hasActiveEditingComponent,
    isCurrentActiveEditingComponent,
    fetchNotebook,
    updateNotebook,
    fetchNotebookStructure,
    fetchNotebookPage,
    createNotebookPage,
    updateNotebookPage,
    deleteNotebookPage,
    reorderNotebookPage,
    fetchNotebookPageNotes,
    createNotebookPageNote,
    updateNotebookPageNote,
    deleteNotebookPageNote,
    reorderNotebookPageNote,
    moveNotebookPageNoteToSpecificPage,
    moveNotebookPageToSpecificPage,
    setEditingComponentId,
    resetEditingComponentId,
    registerSavingComponent,
    getSavingComponentPromise,
    updateSavingComponentState,
    isNotebookLeftPanelExpanded,
    setNotebookEditorLeftPanelExpanded,
    fetchNotebookPageNoteAnnotation,
    updateNotebookPageSpotlight,
    deleteNotebookPageSpotlight,
    toggleSpotlightMenu,
    closeSpotlightMenu,
  };
});
